// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import SoltarArchivos from "../components/funcionales/SoltarArchivos";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import {
  adjuntosDe,
  anularNovedad,
  borrarAdjunto,
  crearNovedad,
  linkAdjunto,
  subirAdjunto,
  traerNovedades,
} from "../services/rrhhNovedades";
import { traerPersonal } from "../services/rrhh";
import { TIPOS, rotuloTipo } from "../shared/rrhhNovedades";
import { rutaDePersona } from "../shared/personas";
import { hoyAr } from "../shared/fechaAr";

/* Novedades del personal: qué le pasó a cada uno, con el papel que lo prueba.
 *
 * ── QUÉ RESUELVE ─────────────────────────────────────────────────────────────
 *
 * Pedido de Nico (25/08/2026): «un sector de novedades. Ahí se dejan registro
 * y documentación de una novedad como enfermedad, suspensión, muerte de un
 * familiar. La idea es que puedas consultar las novedades por empleado».
 *
 * Hasta hoy eso vivía en un cajón y en la memoria de RRHH: la suspensión que
 * se aplicó el año pasado no estaba en ningún lado, y el certificado que la
 * justificaba tampoco.
 *
 * ── NO ES LA PANTALLA «NOVEDADES» DEL INICIO ─────────────────────────────────
 *
 * Aquélla se llama igual y hace lo contrario: DERIVA tarjetas de datos que ya
 * existen, justamente porque nadie escribe novedades a mano. Ésta se escribe a
 * mano y por eso puede existir — que a alguien se le murió el padre no lo sabe
 * ningún sistema, no hay de dónde derivarlo. Por eso el menú la llama
 * «Novedades del personal» y vive en `/rrhh/`.
 *
 * ── DOS LLAVES, LAS MISMAS DE LICENCIAS ──────────────────────────────────────
 *
 * Se mira con `ver.rrhh` y se carga con `cargar.rrhh`, y la pared es la base:
 * sin la primera la consulta vuelve vacía, sin la segunda el insert rebota.
 * Es el mismo tipo de dato que un parte de enfermo, así que es la misma llave
 * — una nueva sólo habría sumado otra que a alguien le falte.
 *
 * ── EL HISTORIAL POR EMPLEADO VIVE EN LA FICHA ───────────────────────────────
 *
 * Esta pantalla es de CARGA y de repaso reciente. «Las novedades de Fulano»,
 * que es la pregunta de Nico, se contesta en la ficha de la persona, donde
 * además se mezclan con las licencias en una sola línea de tiempo. Acá cada
 * fila linkea a su ficha.
 */

const HOY = () => hoyAr(); // el «hoy» argentino, no el de UTC (fechaAr.js)

const fecha = (v) => {
  const s = String(v ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

const RrhhNovedades = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);
  const puedeCargar = puede("cargar.rrhh");

  const [filas, setFilas] = useState(undefined); // undefined = cargando
  const [adjuntos, setAdjuntos] = useState({});
  const [error, setError] = useState(false);
  // Todo el personal activo (no sólo choferes): una novedad puede ser de un
  // gomero o un administrativo, no sólo de quien maneja (P-167).
  const [personal, setPersonal] = useState([]);

  // El formulario.
  const [dni, setDni] = useState("");
  const [buscar, setBuscar] = useState("");
  const [tipo, setTipo] = useState("enfermedad");
  const [desde, setDesde] = useState(HOY());
  const [hasta, setHasta] = useState("");
  const [detalle, setDetalle] = useState("");
  // Los archivos elegidos ANTES de guardar. Se suben después, cuando la
  // novedad ya tiene id: un adjunto necesita de qué colgar.
  const [pendientes, setPendientes] = useState([]);
  const [estado, setEstado] = useState(""); // "" | yendo | mal:<msg>

  const permitido = permisos !== null && permisos.includes("ver.rrhh");

  const cargarTodo = useCallback(() => {
    traerNovedades()
      .then(async (ns) => {
        setFilas(ns);
        // Los adjuntos en lote: una consulta y no una por novedad.
        try {
          setAdjuntos(await adjuntosDe(ns.map((n) => n.id)));
        } catch {
          // Que no se puedan leer los adjuntos NO puede vaciar la pantalla:
          // las novedades ya están y son lo principal.
          setAdjuntos({});
        }
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (!permitido) return;
    cargarTodo();
    traerPersonal()
      .then(setPersonal)
      .catch(() => setPersonal([]));
  }, [permitido, cargarTodo]);

  const nombreDe = useMemo(() => {
    const m = new Map();
    for (const c of personal)
      m.set(String(c.dni), `${c.apellido}, ${c.nombres}`);
    return m;
  }, [personal]);

  const candidatos = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (q.length < 2) return [];
    return personal
      .filter((c) =>
        [c.apellido, c.nombres, c.dni, c.legajo]
          .map((v) => String(v ?? "").toLowerCase())
          .some((v) => v.includes(q)),
      )
      .slice(0, 8);
  }, [personal, buscar]);

  const limpiar = () => {
    setDni("");
    setBuscar("");
    setHasta("");
    setDetalle("");
    setPendientes([]);
    setDesde(HOY());
  };

  const guardar = async (e) => {
    e.preventDefault();
    const d = dni.replace(/\D/g, "");
    if (!d) return setEstado("mal:Falta la persona (elegila o escribí el DNI).");
    if (!desde) return setEstado("mal:Falta cuándo pasó.");
    if (hasta && hasta < desde)
      return setEstado("mal:El período está al revés: termina antes de empezar.");
    setEstado("yendo");
    try {
      const nueva = await crearNovedad({ dni: d, tipo, desde, hasta, detalle });
      // Los adjuntos van DESPUÉS y de a uno. Si alguno falla, la novedad ya
      // quedó: se avisa cuál no subió en vez de tirar todo abajo, porque
      // volver a tipear la novedad entera por un archivo es lo que hace que
      // la gente deje de adjuntar.
      const fallaron = [];
      for (const f of pendientes) {
        try {
          await subirAdjunto(nueva.id, f);
        } catch {
          fallaron.push(f.name);
        }
      }
      limpiar();
      setEstado(
        fallaron.length
          ? `mal:La novedad quedó, pero no subieron: ${fallaron.join(", ")}. Podés adjuntarlos de nuevo desde la fila.`
          : "",
      );
      cargarTodo();
    } catch (err) {
      setEstado(
        "mal:" +
          (/policy|permission|denied|violates/i.test(err?.message || "")
            ? "No se cargó: no tenés permiso."
            : `No se pudo guardar: ${err?.message || "error"}`),
      );
    }
  };

  const anular = async (id) => {
    try {
      await anularNovedad(id);
      cargarTodo();
    } catch {
      setEstado("mal:No se pudo anular. Probá de nuevo en un rato.");
    }
  };

  /* Abrir un adjunto: el link se firma al momento y dura cinco minutos. El
     bucket es privado porque adentro hay certificados médicos con nombre. */
  const abrir = async (a) => {
    try {
      const url = await linkAdjunto(a.ruta);
      if (url) window.open(url, "_blank", "noopener");
    } catch {
      setEstado("mal:No se pudo abrir el archivo.");
    }
  };

  const sacar = async (a) => {
    try {
      await borrarAdjunto(a);
      cargarTodo();
    } catch {
      setEstado("mal:No se pudo sacar el archivo.");
    }
  };

  /* Sumarle archivos a una novedad que ya existe. Es la salida para el que
     consiguió el certificado dos días después — que es lo normal. */
  const sumarA = async (id, archivos) => {
    setEstado("yendo");
    const fallaron = [];
    for (const f of archivos) {
      try {
        await subirAdjunto(id, f);
      } catch {
        fallaron.push(f.name);
      }
    }
    setEstado(fallaron.length ? `mal:No subieron: ${fallaron.join(", ")}.` : "");
    cargarTodo();
  };

  if (!puede("ver.rrhh")) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  const lista = filas || [];

  return (
    <>
      {/* Sólo se dibuja con la llave de escritura: un formulario que la base
          va a rebotar es una promesa rota en diferido. */}
      {puedeCargar && (
        <form className="tk-filtros" onSubmit={guardar} style={{ rowGap: 8 }}>
          <div style={{ position: "relative" }}>
            <input
              type="search"
              placeholder="Persona (nombre, DNI o legajo)"
              value={buscar}
              onChange={(e) => {
                setBuscar(e.target.value);
                setDni("");
              }}
              aria-label="Buscar a la persona"
            />
            {!dni && candidatos.length > 0 && (
              <div className="lic-sugeridos">
                {candidatos.map((c) => (
                  <button
                    key={c.dni}
                    type="button"
                    onClick={() => {
                      setDni(String(c.dni));
                      setBuscar(`${c.apellido}, ${c.nombres}`);
                    }}
                  >
                    {c.apellido}, {c.nombres}{" "}
                    <span className="sub">DNI {c.dni}</span>
                  </button>
                ))}
              </div>
            )}
            {!dni &&
              /^\d{6,9}$/.test(buscar.replace(/\D/g, "")) &&
              candidatos.length === 0 && (
                <button
                  type="button"
                  className="tk-chip"
                  onClick={() => setDni(buscar.replace(/\D/g, ""))}
                >
                  Usar el DNI {buscar.replace(/\D/g, "")}
                </button>
              )}
          </div>

          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            aria-label="Tipo de novedad"
          >
            {TIPOS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>

          <label className="sub">
            desde{" "}
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              aria-label="Desde"
            />
          </label>
          {/* `hasta` es OPCIONAL y se dice: una suspensión termina, un
              fallecimiento no. Obligarlo haría inventar fechas. */}
          <label className="sub">
            hasta{" "}
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              aria-label="Hasta (si termina)"
            />
          </label>

          <input
            type="text"
            placeholder="Detalle"
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            aria-label="Detalle"
          />

          <div style={{ flexBasis: "100%" }}>
            <SoltarArchivos
              deshabilitado={estado === "yendo"}
              onArchivos={(fs) => setPendientes((p) => [...p, ...fs])}
            />
            {pendientes.length > 0 && (
              <div className="nov-adjuntos">
                {pendientes.map((f, i) => (
                  <span className="nov-adjunto" key={`${f.name}-${i}`}>
                    {f.name}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        setPendientes((p) => p.filter((_, j) => j !== i))
                      }
                    >
                      sacar
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <button
            className="btn-primario"
            type="submit"
            disabled={estado === "yendo"}
          >
            {estado === "yendo" ? "Guardando…" : "Cargar novedad"}
          </button>
        </form>
      )}

      {estado.startsWith("mal:") && (
        <div className="panel-aviso suave">
          <p className="panel-aviso__chico">{estado.slice(4)}</p>
        </div>
      )}

      {filas === undefined ? (
        <p className="tenue">Cargando…</p>
      ) : lista.length === 0 ? (
        <div className="panel-aviso suave">
          <h3>Todavía no hay novedades cargadas</h3>
        </div>
      ) : (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Persona</th>
                <th>Qué pasó</th>
                <th>Desde</th>
                <th>Hasta</th>
                <th>Detalle</th>
                <th>Documentación</th>
                <th>Cargó</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((n) => {
                const nombre = nombreDe.get(String(n.dni));
                const suyos = adjuntos[n.id] || [];
                return (
                  <tr key={n.id} className={n.anulado_en ? "tenue" : undefined}>
                    <td>
                      <Link className="rto-link" to={rutaDePersona(n.dni)}>
                        {nombre || `DNI ${n.dni}`}
                      </Link>
                    </td>
                    <td>
                      <span className="pill ojo">{rotuloTipo(n.tipo)}</span>
                      {n.anulado_en && <span className="pill mal">anulada</span>}
                    </td>
                    <td className="dato">{fecha(n.desde)}</td>
                    {/* Sin `hasta` no se inventa un guion largo ni se deja en
                        blanco: se dice que no tiene fin. */}
                    <td className="dato">
                      {n.hasta ? fecha(n.hasta) : <span className="sub">sin fin</span>}
                    </td>
                    <td>{n.detalle || <span className="sub">—</span>}</td>
                    <td>
                      {suyos.length === 0 && (
                        <span className="sub">sin documentación</span>
                      )}
                      <div className="nov-adjuntos">
                        {suyos.map((a) => (
                          <span className="nov-adjunto" key={a.id}>
                            <button
                              type="button"
                              className="link-button"
                              onClick={() => abrir(a)}
                            >
                              {a.nombre_original || "archivo"}
                            </button>
                            {puedeCargar && !n.anulado_en && (
                              <button
                                type="button"
                                className="link-button"
                                onClick={() => sacar(a)}
                                title="Sacar el archivo (la novedad queda)"
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {puedeCargar && !n.anulado_en && (
                        <SoltarArchivos
                          deshabilitado={estado === "yendo"}
                          onArchivos={(fs) => sumarA(n.id, fs)}
                        />
                      )}
                    </td>
                    <td className="sub">{n.usuario_nombre || n.usuario || "—"}</td>
                    <td>
                      {puedeCargar && !n.anulado_en && (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => anular(n.id)}
                        >
                          anular
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default RrhhNovedades;
